import {
	faCheckCircle,
	faExclamationCircle,
	faExclamationTriangle,
	faInfoCircle,
	faTimes
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useCallback, useEffect, useState } from "react";
import { ToastProps } from "./Toast.types";

const toastConfig = {
	success: {
		icon: faCheckCircle,
		iconBgColor: "bg-green-100",
		iconColor: "text-green-600",
		bgColor: "bg-green-50",
		borderColor: "border-green-200"
	},
	error: {
		icon: faExclamationCircle,
		iconBgColor: "bg-red-100",
		iconColor: "text-red-600",
		bgColor: "bg-red-50",
		borderColor: "border-red-200"
	},
	warning: {
		icon: faExclamationTriangle,
		iconBgColor: "bg-yellow-100",
		iconColor: "text-yellow-600",
		bgColor: "bg-yellow-50",
		borderColor: "border-yellow-200"
	},
	info: {
		icon: faInfoCircle,
		iconBgColor: "bg-blue-100",
		iconColor: "text-blue-600",
		bgColor: "bg-blue-50",
		borderColor: "border-blue-200"
	}
};

const buttonVariants = {
	primary:
		"px-3 py-1 text-sm text-white bg-blue-600 rounded hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500",
	secondary:
		"px-3 py-1 text-sm text-gray-700 bg-gray-100 rounded hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500",
	danger:
		"px-3 py-1 text-sm text-white bg-red-600 rounded hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500",
	warning:
		"px-3 py-1 text-sm text-white bg-yellow-600 rounded hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500"
};

interface ToastItemProps extends ToastProps {
	onRemove: (id: string) => void;
	autoClose?: number;
	pauseOnHover?: boolean;
	hideProgressBar?: boolean;
	closeOnClick?: boolean;
	draggable?: boolean;
}

export default function Toast({
	id,
	type,
	title,
	message,
	duration,
	actions,
	showCloseButton = true,
	onClose,
	onRemove,
	autoClose = 3000,
	pauseOnHover = true,
	hideProgressBar = false,
	closeOnClick = true,
	draggable = true
}: ToastItemProps) {
	const [isVisible, setIsVisible] = useState(false);
	const [isPaused, setIsPaused] = useState(false);
	const [progress, setProgress] = useState(100);
	const [isDragging, setIsDragging] = useState(false);

	const config = toastConfig[type];
	const effectiveDuration = duration || autoClose;

	const handleClose = useCallback(() => {
		setIsVisible(false);
		setTimeout(() => {
			onClose?.();
			onRemove(id);
		}, 300);
	}, [onClose, onRemove, id]);

	useEffect(() => {
		setIsVisible(true);
	}, []);

	useEffect(() => {
		if (effectiveDuration <= 0 || isPaused) return;

		const interval = setInterval(() => {
			setProgress((prev) => {
				const newProgress = prev - 100 / (effectiveDuration / 100);
				if (newProgress <= 0) {
					handleClose();
					return 0;
				}
				return newProgress;
			});
		}, 100);

		return () => clearInterval(interval);
	}, [effectiveDuration, isPaused, handleClose]);

	const handleMouseEnter = () => {
		if (pauseOnHover) setIsPaused(true);
	};

	const handleMouseLeave = () => {
		if (pauseOnHover) setIsPaused(false);
	};

	const handleClick = () => {
		if (closeOnClick && !isDragging) handleClose();
	};

	const handleMouseDown = () => {
		if (!draggable) return;
		setIsDragging(true);
	};

	const handleMouseMove = (e: React.MouseEvent) => {
		if (!isDragging || !draggable) return;
		e.preventDefault();
	};

	const handleMouseUp = () => {
		if (isDragging) {
			setTimeout(() => setIsDragging(false), 100);
		}
	};

	return (
		<div
			className={`relative bg-white rounded-lg shadow-lg border ${config.borderColor} p-4 mb-3 cursor-pointer transition-all duration-300 transform ${
				isVisible ? "translate-x-0 opacity-100" : "translate-x-full opacity-0"
			} ${isDragging ? "scale-105 shadow-xl" : ""} max-w-sm`}
			onMouseEnter={handleMouseEnter}
			onMouseLeave={handleMouseLeave}
			onClick={handleClick}
			onKeyDown={(e) => {
				if (e.key === "Enter" || e.key === " ") {
					handleClick();
				}
			}}
			onMouseDown={handleMouseDown}
			onMouseMove={handleMouseMove}
			onMouseUp={handleMouseUp}
			role="alert"
			aria-live="polite"
		>
			<div className="flex items-center">
				<div className="flex-shrink-0 mr-3">
					<div
						className={`flex items-center justify-center h-8 w-8 rounded-full ${config.iconBgColor}`}
					>
						<FontAwesomeIcon
							icon={config.icon}
							className={`h-4 w-4 ${config.iconColor}`}
						/>
					</div>
				</div>

				<div className="flex-1 min-w-0">
					{title && (
						<h4 className="text-xs font-normal text-gray-400 mb-1">{title}</h4>
					)}
					<div className="text-sm text-gray-700">
						{typeof message === "string" ? message : message}
					</div>

					{actions && actions.length > 0 && (
						<div className="flex gap-2 mt-3">
							{actions.map((action, index) => (
								<button
									key={`${action.label}-${index}`}
									type="button"
									onClick={(e) => {
										e.stopPropagation();
										action.onClick();
									}}
									className={buttonVariants[action.variant || "secondary"]}
								>
									{action.label}
								</button>
							))}
						</div>
					)}
				</div>

				{showCloseButton && (
					<button
						type="button"
						onClick={(e) => {
							e.stopPropagation();
							handleClose();
						}}
						className="flex-shrink-0 ml-2 text-gray-400 hover:text-gray-600 focus:outline-none"
						aria-label="Close toast"
					>
						<FontAwesomeIcon icon={faTimes} className="h-4 w-4" />
					</button>
				)}
			</div>

			{!hideProgressBar && effectiveDuration > 0 && (
				<div className="absolute bottom-0 left-0 w-full h-1 bg-gray-200 rounded-b-lg overflow-hidden">
					<div
						className={`h-full transition-all duration-100 ${
							type === "success"
								? "bg-green-500"
								: type === "error"
									? "bg-red-500"
									: type === "warning"
										? "bg-yellow-500"
										: "bg-blue-500"
						}`}
						style={{ width: `${progress}%` }}
					/>
				</div>
			)}
		</div>
	);
}
